import { addMinutes } from 'date-fns';
import { Response } from 'miragejs';
import {
  ChangePasswordResult,
  Civilite,
  DayOfWeek,
  GetUserInfoResult,
  GetUserLikesResult,
  LoginWithPasswordResult,
  LoginWithSsoResult,
  PaymentTypeValues,
  RefreshTokenResult,
  UpdateAccountInfosResult,
  UpdateEmailResult,
} from '../api';
import { AppErrorKeys } from '../errors/error';
import { getErrorResponse, getSuccessEmptyResponse } from './common';

const weekDays = [DayOfWeek.Monday, DayOfWeek.Tuesday, DayOfWeek.Wednesday, DayOfWeek.Thursday, DayOfWeek.Friday];

export const MOCK_USER_INFO_OK: GetUserInfoResult = {
  userInfo: {
    id: 1,
    displayName: 'Cécile Champion',
    email: 'c.champion@yopmail.com',
    firstName: 'Cécile',
    language: 'fr',
    lastName: 'Champion',
    phoneNumber: undefined,
    civilite: Civilite.Mme,
    balanceAlertAmount: 5,
    canChangePassword: true,
    sites: [
      {
        cdpf: '048161',
        libelleLong: 'Tour Égée',
        marqueName: 'Tour Égée',
        name: 'Tour Égée',
        solde: 12.34,
        compteClientNotFound: false,
        bookingMaxDays: 10,
        bookingMinDays: 2,
        bookingOpenedDays: weekDays,
        qrCodeInformation: '123ABDG52455',
        hasCashRegister: true,
        registrationWithoutCashRegister: false,
        balanceAlertMaximumAmount: 20,
        balanceAlertSiteAmount: 4,
        balanceAlertSiteIsActive: true,
        restaurantsDisplayed: true,
        showBooking: true,
        askPresenceOnSiteHeureArrivee: true,
        showCalories: true,
        showNutriscore: true,
        showCarbonscore: true,
        showGreenscore: true,
        showTypologies: true,
        paymentType: PaymentTypeValues.PostPaiement,
      },
    ],
  },
};

export const MOCK_USER_INFO = {
  OK: MOCK_USER_INFO_OK,
  KO: getErrorResponse(AppErrorKeys.AnErrorOccured),
};

const MOCK_LOGIN_OK: LoginWithPasswordResult = {
  authToken: {
    accessToken: 'accessToken',
    accessTokenExpirationDate: addMinutes(new Date(), 20).toISOString(),
    refreshToken: 'refreshToken',
  },
};

export const MOCK_LOGIN = {
  OK: () => MOCK_LOGIN_OK,
  CONFIRMED_EMAIL_REQUIRED: getErrorResponse(AppErrorKeys.ConfirmedEmailRequired),
};

const MOCK_LOGIN_SSO_OK: LoginWithSsoResult = {
  authToken: { accessToken: 'accessToken', accessTokenExpirationDate: new Date(2025, 1, 1).toISOString(), refreshToken: 'refreshToken' },
};

const MOCK_UPDATE_ACCOUNT_INFOS_OK: UpdateAccountInfosResult = {
  authToken: { accessToken: 'accessToken', accessTokenExpirationDate: new Date(2025, 1, 1).toISOString(), refreshToken: 'refreshToken' },
};

const MOCK_CHANGE_PASSWORD_OK: ChangePasswordResult = {
  authToken: { accessToken: 'accessToken', accessTokenExpirationDate: new Date(2025, 1, 1).toISOString(), refreshToken: 'refreshToken' },
};

export const MOCK_REFRESH_TOKEN: { OK: () => RefreshTokenResult } = {
  OK: () => ({
    authToken: {
      accessToken: 'accessToken',
      accessTokenExpirationDate: addMinutes(new Date(), 20).toISOString(),
      refreshToken: 'refreshToken',
    },
  }),
};

export const MOCK_LOGIN_SSO = {
  OK: MOCK_LOGIN_SSO_OK,
};

export const MOCK_UPDATE_ACCOUNT_INFOS = {
  OK: MOCK_UPDATE_ACCOUNT_INFOS_OK,
  KO: getErrorResponse(AppErrorKeys.AnErrorOccured),
};

export const MOCK_UPDATE_PREFERRED_LANGUAGE_INFOS = {
  OK: getSuccessEmptyResponse(),
  KO: getErrorResponse(AppErrorKeys.AnErrorOccured),
};

const MOCK_UPDATE_ACCOUNT_EMAIL_OK: UpdateEmailResult = {
  authToken: { accessToken: 'accessToken', accessTokenExpirationDate: new Date(2025, 1, 1).toISOString(), refreshToken: 'refreshToken' },
};

export const MOCK_UPDATE_ACCOUNT_EMAIL = {
  OK: MOCK_UPDATE_ACCOUNT_EMAIL_OK,
  KO: getErrorResponse(AppErrorKeys.AnErrorOccured),
};

export const MOCK_CHANGE_PASSWORD = {
  OK: MOCK_CHANGE_PASSWORD_OK,
  KO: getErrorResponse(AppErrorKeys.AnErrorOccured),
};

export const MOCK_UPDATE_BALANCE_ALERT = {
  OK: true,
  KO: getErrorResponse(AppErrorKeys.AnErrorOccured),
};

export const MOCK_UPDATE_LIKE_NOTIFICATION: { OK: Response; KO: Response } = {
  OK: getSuccessEmptyResponse(),
  KO: getErrorResponse(AppErrorKeys.AnErrorOccured),
};

const MOCK_GET_LIKES_OK: GetUserLikesResult = {
  dishFamilies: [
    {
      label: 'Entrées',
      dishes: [
        {
          label: 'Concombre en gaspacho',
          id: 1,
          isNotificationEnabled: true,
          canBeNotified: true,
        },
        {
          label: 'Carottes à la coriandre',
          id: 2,
          isNotificationEnabled: true,
          canBeNotified: true,
        },
        {
          label: 'Carottes à la coriandre et chantilly',
          id: 3,
          isNotificationEnabled: false,
          canBeNotified: true,
        },
        {
          label: 'Carottes à la coriandre, chantilly au cumin',
          id: 4,
          isNotificationEnabled: false,
          canBeNotified: true,
        },
        {
          label: 'Carottes à la coriandre et aux fraises',
          id: 5,
          isNotificationEnabled: false,
          canBeNotified: true,
        },
      ],
    },
    {
      label: 'Plats',
      dishes: [
        {
          label: 'L’authentique burger veggie du Chef',
          id: 6,
          isNotificationEnabled: false,
          canBeNotified: true,
        },
        {
          label: 'Petit farcis au caviar d’aubergine',
          id: 7,
          isNotificationEnabled: false,
          canBeNotified: true,
        },
        {
          label: 'Légumes grillés, feta AOP et basilic',
          id: 8,
          isNotificationEnabled: false,
          canBeNotified: true,
        },
        {
          label: 'Boeuf bourguignon',
          id: 9,
          isNotificationEnabled: false,
          canBeNotified: true,
        },
      ],
    },
    {
      label: 'Desserts',
      dishes: [
        {
          label: 'Mousse au chocolat',
          id: 10,
          isNotificationEnabled: true,
          canBeNotified: true,
        },
        {
          label: 'Triple caramel chocolat salé',
          id: 11,
          isNotificationEnabled: true,
          canBeNotified: true,
        },
        {
          label: 'Mousse au chocolat',
          id: 12,
          isNotificationEnabled: false,
          canBeNotified: true,
        },
        {
          label: 'Triple caramel chocolat salé',
          id: 13,
          isNotificationEnabled: false,
          canBeNotified: true,
        },
      ],
    },
  ],
};

export const MOCK_GET_LIKES: { OK: GetUserLikesResult; OK_EMPTY: GetUserLikesResult; KO: Response } = {
  OK: MOCK_GET_LIKES_OK,
  OK_EMPTY: { dishFamilies: [] },
  KO: getErrorResponse(AppErrorKeys.AnErrorOccured),
};
